import React, { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, Send, Pencil, X, Plus, Mail } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { clientService } from '../../api';

export const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export const DocItemRow = ({ item, onUpload, onToggle, busy, compact }) => {
  const ref = useRef(null);
  const received = item.status === 'received';
  return (
    <div className={`flex items-center gap-3 ${compact ? 'px-3 py-2' : 'px-5 py-3'}`} data-testid={`doc-item-${item.id}`}>
      <Checkbox checked={received} onCheckedChange={() => onToggle(item)} className="h-5 w-5 data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-700" data-testid={`doc-item-checkbox-${item.id}`} aria-label={`Mark ${item.label} received`}/>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${received ? 'text-stone-500 line-through decoration-stone-300' : 'text-stone-900'}`}>{item.label}</div>
        <div className="text-xs text-stone-500 truncate">
          {received
            ? <>Received{item.uploadedBy ? ` from ${item.uploadedBy}` : ''} {fmt(item.uploadedAt)}{item.fileId && <> · <a href={clientService.fileUrl(item.fileId)} target="_blank" rel="noreferrer" className="text-emerald-800 hover:underline" data-testid={`doc-item-file-${item.id}`}>{item.fileName}</a></>}</>
            : 'Waiting on client'}
        </div>
      </div>
      <input ref={ref} type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) onUpload(item, e.target.files[0]); e.target.value = ''; }}/>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} title="Upload file" data-testid={`doc-item-upload-${item.id}`}>
        {busy ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
      </Button>
    </div>
  );
};

const ChecklistEditor = ({ data, onSave, onCancel }) => {
  const [items, setItems] = useState(data.checklist);
  const [draft, setDraft] = useState('');
  const add = () => { if (draft.trim()) { setItems([...items, draft.trim()]); setDraft(''); } };
  return (
    <div className="p-5 space-y-3 bg-stone-50 border-b border-stone-100" data-testid="checklist-editor">
      {items.map((it, i) => (
        <div key={`${it}-${i}`} className="flex items-center gap-2">
          <Input value={it} onChange={(e) => setItems(items.map((x, j) => (j === i ? e.target.value : x)))} className="h-9 bg-white"/>
          <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-stone-400 hover:text-red-600" data-testid={`checklist-remove-${i}`}><X size={16}/></button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder="Add a document, e.g. Fuel slips" className="h-9 bg-white" data-testid="checklist-new-input"/>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="checklist-add-btn"><Plus size={14}/></Button>
      </div>
      <div className="flex justify-between gap-2 pt-1">
        <button type="button" onClick={() => setItems(data.defaults)} className="text-xs text-stone-500 hover:text-stone-800 underline">Reset to defaults</button>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900 text-white" onClick={() => onSave(items)} data-testid="checklist-save-btn">Save checklist</Button>
        </div>
      </div>
    </div>
  );
};

const RemindersLog = ({ reminders }) => (
  <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="reminders-log">
    <div className="p-5 border-b border-stone-100 flex items-center gap-2">
      <Mail size={16} className="text-emerald-800"/>
      <div className="font-semibold text-stone-900 text-sm">Reminder history</div>
      <span className="ml-auto text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="reminders-mocked-badge">Mocked · not emailed</span>
    </div>
    {reminders.length === 0 && <div className="p-5 text-sm text-stone-500 text-center">No reminders yet. They&apos;re logged automatically 5 days before a VAT, PAYE or management accounts deadline when documents are missing.</div>}
    <div className="divide-y divide-stone-100">
      {reminders.map((r) => (
        <div key={r.id} className="px-5 py-3 text-sm" data-testid={`reminder-${r.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-stone-900">{r.subject}</span>
            <span className="text-xs text-stone-500">{fmt(r.createdAt)} · {r.trigger}</span>
          </div>
          <div className="text-xs text-stone-600 mt-0.5">To {r.to}: {r.body}</div>
        </div>
      ))}
    </div>
  </div>
);

export const DocumentsPanel = ({ clientId, onChange }) => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [editing, setEditing] = useState(false);
  const [busyItem, setBusyItem] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => Promise.all([clientService.documents(clientId), clientService.reminders(clientId)])
    .then(([d, r]) => { setData(d); setReminders(r); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientId]);

  const err = (title) => (e) => toast({ title, description: e?.response?.data?.detail || 'Try again.', variant: 'destructive' });
  const refresh = () => { load(); onChange?.(); };

  const upload = (req) => (item, file) => {
    setBusyItem(item.id);
    clientService.uploadItem(clientId, req.id, item.id, file).then(() => { toast({ title: 'Document uploaded', description: item.label }); refresh(); })
      .catch(err('Upload failed')).finally(() => setBusyItem(null));
  };
  const toggle = (req) => (item) => clientService.setItemStatus(clientId, req.id, item.id, item.status === 'received' ? 'missing' : 'received').then(refresh).catch(err('Update failed'));
  const saveChecklist = (items) => clientService.updateChecklist(clientId, items).then((d) => { setData(d); setEditing(false); toast({ title: 'Checklist saved' }); onChange?.(); }).catch(err('Save failed'));
  const sendNow = () => { setSending(true); clientService.sendReminder(clientId).then(() => { toast({ title: 'Reminder logged (mocked)', description: 'Email sending is not connected yet.' }); load(); }).catch(err('Could not send')).finally(() => setSending(false)); };

  if (!data) return <div className="p-10 flex justify-center text-stone-500"><Loader2 className="animate-spin" size={18}/></div>;
  const [current, ...past] = data.requests;
  const missing = current ? current.items.filter((i) => i.status !== 'received').length : 0;

  return (
    <div className="space-y-6" data-testid="documents-panel">
      <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
        <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold text-stone-900">Documents for {current?.periodLabel || 'this month'}</div>
            <div className="text-xs text-stone-500 mt-0.5" data-testid="doc-request-summary">{current ? `${current.items.length - missing} of ${current.items.length} received` : 'No checklist items'} · tick items off as they arrive, or the client uploads them from their portal</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(!editing)} data-testid="checklist-edit-btn"><Pencil size={14} className="mr-1.5"/> Edit checklist</Button>
            <Button size="sm" disabled={sending || missing === 0} onClick={sendNow} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="send-reminder-btn">
              {sending ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Send size={14} className="mr-1.5"/>} Send reminder
            </Button>
          </div>
        </div>
        {editing && <ChecklistEditor data={data} onSave={saveChecklist} onCancel={() => setEditing(false)}/>}
        <div className="divide-y divide-stone-100">
          {current?.items.map((it) => <DocItemRow key={it.id} item={it} busy={busyItem === it.id} onUpload={upload(current)} onToggle={toggle(current)}/>)}
        </div>
      </div>

      {past.length > 0 && (
        <details className="rounded-xl bg-white border border-stone-200 overflow-hidden">
          <summary className="p-5 cursor-pointer text-sm font-medium text-stone-700">Previous months · {past.length}</summary>
          {past.map((r) => (
            <div key={r.id} className="border-t border-stone-100">
              <div className="px-5 pt-3 text-xs uppercase tracking-wider font-semibold text-stone-500">{r.periodLabel}</div>
              {r.items.map((it) => <DocItemRow key={it.id} item={it} busy={busyItem === it.id} onUpload={upload(r)} onToggle={toggle(r)}/>)}
            </div>
          ))}
        </details>
      )}

      <RemindersLog reminders={reminders}/>
    </div>
  );
};
